/** tm/api-report.ts — 동행상담 결과 리포트 API 래퍼
 *  api.ts 의 규약({success,data,error})을 그대로 따르고, 응답 검사를 한 곳에 모은다.
 */

export interface ReportItem { no: string; text: string; bullets?: string[] }
export interface ReportTodo {
  no: string; task: string; note?: string; tag?: string; owner: string; due: string;
  dueAuto?: boolean;   // 하위 기한을 묶어 채운 값(리포트를 다시 만들 때 다시 묶음 · 직접 고치면 false)
  costs: Array<{ label?: string; amount: string; suffix?: string; note?: string }>;
  subs?: Array<{ no: string; task: string; owner: string; due: string; costs: [] }>;
}
export interface ReviewRow {
  no: string; before: string; after: string;
  beforeBullets: string[]; afterBullets: string[];
  changed: boolean; warnings: string[];
}
export interface ReportState {
  report_status: 'none' | 'generating' | 'draft' | 'ready' | 'sent' | 'failed' | 'stale';
  report_version: number;
  report_error: string | null;
  report_generated_at: string | null;
  memo_pdf_name: string | null;
  has_memo: boolean;
  content_company: string;
  content_consult: string;
  report: (Record<string, unknown> & {
    company?: ReportItem[]; consult?: ReportItem[]; todos?: ReportTodo[];
    _warnings?: string[]; _link?: string;
    _review?: { company: ReviewRow[]; consult: ReviewRow[]; aiUsed: boolean; aiError: string | null };
    _render?: { height: number; compressStage: number; overflow: boolean; fontsOk: boolean };
    _pages?: number;
  }) | null;
  download_code: string | null;
}

const token = () => localStorage.getItem('token') || '';

async function req<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`/api/tm${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token() },
    body: body === undefined ? undefined : JSON.stringify(body),
    cache: 'no-store',
  });
  const json = await res.json().catch(() => ({ success: false, error: '응답을 읽을 수 없습니다' }));
  if (!res.ok || json.success === false) throw new Error(json.error || `요청 실패 (${res.status})`);
  return json.data as T;
}

/** 상담메모 PDF 업로드 (HWP 는 "PDF로 저장" 후 올린다) */
export async function uploadMemo(consultId: string, file: File) {
  const fd = new FormData();
  fd.append('file', file);
  const res = await fetch(`/api/tm/consultations/${consultId}/report/memo`, {
    method: 'POST', headers: { Authorization: 'Bearer ' + token() }, body: fd,
  });
  const json = await res.json().catch(() => ({ success: false, error: '업로드 실패' }));
  if (!res.ok || json.success === false) throw new Error(json.error || '업로드 실패');
  return json.data as { memo_pdf_name: string; size: number };
}

/** 메모 → 01·02·03 초안 */
export const extractDraft = (consultId: string) =>
  req<{ company: string; consult: string; todos: ReportTodo[]; aiUsed: boolean; aiError: string | null }>(
    'POST', `/consultations/${consultId}/report/extract`);

/** 저장 없이 다듬기 미리보기 — 검수 화면의 [다시 다듬기] 가 이걸 쓴다 */
export const polishPreview = (consultId: string, company: string, consult: string) =>
  req<{ company: ReportItem[]; consult: ReportItem[]; review: { company: ReviewRow[]; consult: ReviewRow[] }; aiUsed: boolean; aiError: string | null }>(
    'POST', `/consultations/${consultId}/report/polish`, { company, consult });

/** 생성·재생성 (즉시 202, 결과는 getReport 로 확인) */
export const generateReport = (consultId: string, body?: { content?: unknown; todos?: unknown; override?: unknown; content_company?: string; content_consult?: string }) =>
  req<{ report_status: string }>('POST', `/consultations/${consultId}/report/generate`, body || {});

export const getReport = (consultId: string) =>
  req<ReportState>('GET', `/consultations/${consultId}/report`);

// ── 발송 ────────────────────────────────────────────────────
export interface DispatchItem {
  kind: 'fp' | 'wm';
  key?: string;   // 받는 분마다 하나(WM 여러 분)
  name: string;
  phone: string;
  portalUrl: string;
  reportUrl: string;
  message: string;
  selected: boolean;
  blocked: string | null;
  bytes: number;
}
export interface DispatchEntry {
  version: number;
  at: string;
  results: Array<{ kind: string; name: string; phone: string; ok: boolean; error: string | null }>;
}
export interface DispatchPreview {
  ready: boolean;
  reason?: string | null;
  version: number;
  companyName?: string;
  alreadySent: DispatchEntry | null;
  items: DispatchItem[];
}

/** 미리보기 — 아무것도 보내지 않는다 */
export const getDispatchPreview = (consultId: string) =>
  req<DispatchPreview>('GET', `/consultations/${consultId}/report/dispatch-preview`);

/** 실제 발송 — 화면에서 확인한 문장을 그대로 보낸다 */
export const sendDispatch = (consultId: string, items: Array<{ kind: string; name: string; phone: string; message: string }>) =>
  req<{ ok: boolean; version: number; results: DispatchEntry['results'] }>(
    'POST', `/consultations/${consultId}/report/dispatch`, { items });

/** 검수 결과 저장 → 재렌더 */
export const saveReview = (consultId: string, content: { company: ReportItem[]; consult: ReportItem[]; todos: ReportTodo[] }, override?: unknown) =>
  req<{ version: number; pageCount: number; warnings: string[]; code: string }>(
    'PUT', `/consultations/${consultId}/report`, { content, override });

/** 화면에 바로 띄우는 미리보기 URL (inline) */
export const getPreviewUrl = (consultId: string) =>
  req<{ url: string; version: number }>('GET', `/consultations/${consultId}/report/preview`);

export const getDownloadUrl = (consultId: string) =>
  req<{ url: string; name: string }>('GET', `/consultations/${consultId}/report/download`);

/** 검수에서 되돌린 표현을 보호 용어로 등록 — 다음부터 AI 가 건드리지 않는다 */
export const protectTerm = (term: string, memo?: string) =>
  req<{ term: string }>('POST', '/report/protected-terms', { term, memo });

/** 비용·담당·기한표기·태그의 과거 입력 이력 (클릭 선택용) */
export const getPresets = (kind: 'cost' | 'owner' | 'due' | 'tag') =>
  req<Array<{ id: number; kind: string; label: string; value: Record<string, unknown>; use_count: number }>>(
    'GET', `/report/presets?kind=${kind}`);

export const addPreset = (kind: 'cost' | 'owner' | 'due' | 'tag', label: string, value: Record<string, unknown>) =>
  req<{ id: number }>('POST', '/report/presets', { kind, label, value });
